import { uniqBy } from "lodash-es";
import { CompetitorTypes } from "~enums/competitorTypes";
import type { SeasonEntrant } from "~pages/FinalStandings/types/seasonEntrant";
import type { TransformedEntrant } from "~pages/FinalStandings/types/transformedEntrant";
import type { PlacingEntity, PlacingEntityResponse } from "~types/placingEntity";
import type { PlacingPerson, PlacingPersonResponse } from "~types/placingPerson";
import type { SeasonEntity, SeasonEntityResources, SeasonEntityResponse } from "~types/seasonEntity";
import type { SeasonPerson, SeasonPersonResources, SeasonPersonResponse } from "~types/seasonPerson";

const transformEntrant = (entrantResponse: SeasonEntityResponse | SeasonPersonResponse, competitorType) => {
  const resources: SeasonEntityResources | SeasonPersonResources | undefined = entrantResponse?.includes?.resources;
  const isEntityEntrant = (p: SeasonEntrant): p is SeasonEntity => "entityId" in p;
  const isPersonEntrant = (p: SeasonEntrant): p is SeasonPerson => "personId" in p;

  const data: SeasonEntrant[] = entrantResponse?.data || [];
  const keyForUniq = competitorType === CompetitorTypes.Entity ? "entityId" : "personId";
  const entrantData = uniqBy(data, keyForUniq);

  return entrantData.map((entrant: SeasonEntrant) => ({
    ...entrant,
    person: isPersonEntrant(entrant) && resources?.[entrant?.person?.resourceType ?? ""]?.[entrant?.person?.id ?? ""],
    entity: isEntityEntrant(entrant) && resources?.[entrant?.entity?.resourceType ?? ""]?.[entrant?.entity?.id ?? ""],
  }));
};

export const transformPlacingResponse = (
  entrantResponse: SeasonEntityResponse | SeasonPersonResponse,
  placingsResponse: PlacingEntityResponse | PlacingPersonResponse,
  competitorType: CompetitorTypes,
) => {
  const entrants: Array<TransformedEntrant> = transformEntrant(entrantResponse, competitorType);
  const placings = placingsResponse.data;

  const isEntityPlacing = (p: PlacingEntity | PlacingPerson): p is PlacingEntity => "entityId" in p;
  const isPersonPlacing = (p: PlacingEntity | PlacingPerson): p is PlacingPerson => "personId" in p;

  return entrants.map((entrant: TransformedEntrant) => {
    const placingData = placings.find((placing) => {
      if (isEntityPlacing(placing)) {
        return placing.entityId === entrant.entity?.entityId;
      } else if (isPersonPlacing(placing)) {
        return placing.personId === entrant.person?.personId;
      }
      return false;
    });
    return placingData ? { ...placingData, ...entrant } : entrant;
  });
};
